import React, { ChangeEvent, FormEvent } from "react";
import { RouteComponentProps, Prompt } from "react-router";

import withTitle from "../withTitle";
import { V2 } from "../../Api";
import { formatDateTimeLocalNumeric, formatName } from "../../Utilities";
import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ValidateInput from "../ValidateInput";
import ExpandList, { ExpandListEvent } from "../ExpandList";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";
import DialogBase from "../DialogBase";
import HasRight from "../HasRight";

interface VehicleEditViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    isLoadingTrackers: boolean | string;
    hasChanged: boolean;
    isEdit: boolean;
    isDeletionDialogOpen: boolean;
    isLinkDialogOpen: boolean;

    id: string;

    color: string;
    description: string;
    tag: string;
    vin: string;
    status: string;
    employeeId: string;
    spareKey: string;

    keys: any[];

    log: any[];
    tracker?: any;
    trackers: any[];
    pendingTrackerId: string | null;
    selectedTrackerId: string;

    employees: any[];
}

class VehicleEditView extends React.Component<RouteComponentProps<any>, VehicleEditViewState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: VehicleEditViewState = {
            isLoading: true,
            isSubmitting: false,
            isLoadingTrackers: false,
            hasChanged: false,
            isEdit: !!props.match.params.id,
            isDeletionDialogOpen: false,
            isLinkDialogOpen: false,

            id: props.match.params.id,

            color: "",
            description: "",
            tag: "",
            vin: "",
            status: "0",
            employeeId: "",
            spareKey: "",

            keys: [],

            log: [],

            trackers: [],
            pendingTrackerId: null,
            selectedTrackerId: "",

            employees: [],
        };

        this.state = state;

        this.inputs = [];

        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.addKey = this.addKey.bind(this);
        this.deleteKey = this.deleteKey.bind(this);
        this.showDeletionDialog = this.showDeletionDialog.bind(this);
        this.closeDeletionDialog = this.closeDeletionDialog.bind(this);
        this.deleteVehicle = this.deleteVehicle.bind(this);
        this.openTrackerLinkDialog = this.openTrackerLinkDialog.bind(this);
        this.closeTrackerLinkDialog = this.closeTrackerLinkDialog.bind(this);
        this.handleTrackerLinkSubmit = this.handleTrackerLinkSubmit.bind(this);
        this.unlinkTracker = this.unlinkTracker.bind(this);
    }

    private receiveValidationRef(ref: ValidateInput) {
        this.inputs.push(ref);
    }

    public async componentDidMount() {
        if (!this.state.isEdit) {
            this.setState({
                isLoading: false
            });

            return;
        }

        try {
            const [vehicle, employees] = await Promise.all([
                V2.get<any>(`vehicles/${this.state.id}`),
                V2.get<any[]>("vehicles/users"),
            ]);

            (employees as any[]).sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));

            this.setState({
                color: vehicle.color,
                description: vehicle.name,
                tag: vehicle.tag,
                vin: vehicle.vin,
                keys: vehicle.keys,
                log: vehicle.log || [],
                status: vehicle.status.toString(),
                employees: employees as any[],
                employeeId: vehicle.employeeId,
                tracker: vehicle.tracker,
                spareKey: vehicle.spareKeyNote,
            });

            this.setState({
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load vehicle"
            });
        }
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }

        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-text">
                    {this.state.isLoading}
                </div>
            </div>;
        }

        return (
            <div className="viewer-root">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="veh:delete" condition={this.state.isEdit}>
                            <button type="button" onClick={this.showDeletionDialog}><i className="icon-trash" /><span>Delete vehicle...</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <DeletionConfirmationDialog isOpen={this.state.isDeletionDialogOpen} description={null} onRequestClose={this.closeDeletionDialog} onConfirm={this.deleteVehicle} onSuccess={() => this.props.history.goBack()}>
                    <p>
                        Are you sure you want to delete <b><code>{this.state.tag || "[NO TAG]"}</code></b>?<br />
                    </p>
                    <p>
                        Tag: <b><code>{this.state.tag}</code></b><br />
                        Description: <b>{this.state.description}</b>
                    </p>
                    <div className="warning-box slim">
                        <small><em><b>Note:</b> This will also delete all logs relating to this vehicle.</em></small>
                    </div>
                </DeletionConfirmationDialog>
                <DialogBase title="Link Tracker" className="modal-content-medium" isOpen={this.state.isLinkDialogOpen} onRequestClose={this.closeTrackerLinkDialog} onSubmit={this.handleTrackerLinkSubmit}>
                    <p>
                        Select an unused tracker below to link to {this.state.tag || this.state.description}
                    </p>
                    <table className="table-vertical">
                        <thead>
                            <tr>
                                <th className="col-smallest" />
                                <th>Name</th>
                                <th className="col-smallest">State</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.isLoadingTrackers === true
                                ? <tr><td className="col-center" colSpan={10}>Fetching trackers... <span className="spinner lds-dual-ring-small" /></td></tr>
                                : typeof this.state.isLoadingTrackers === "string"
                                    ? <tr><td colSpan={10}><div className="error-text">{this.state.isLoadingTrackers}</div></td></tr>
                                    : this.state.trackers.map(a => <tr key={a.id} tabIndex={0} className={"clickable" + (this.state.selectedTrackerId === a.id ? " row-selected" : "")} onClick={() => this.setState({ selectedTrackerId: a.id })}>
                                        <td>{this.state.selectedTrackerId === a.id ? <i className="icon-check" /> : " "}</td>
                                        <td>{a.displayName}</td>
                                        <td>{a.status || "Unknown"}</td>
                                    </tr>)}
                        </tbody>
                    </table>
                    &nbsp;
                    <div className="action-panel-dialog">
                        <button type="submit" className="button push-right" disabled={!this.state.selectedTrackerId}>Link</button>
                        <button type="button" className="button" onClick={this.closeTrackerLinkDialog}>Cancel</button>
                    </div>
                </DialogBase>
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Basic Information</h3>
                            </header>
                            <section>
                                <div className="input-form-group col-75">
                                    <label htmlFor="frm-description" className="required">Vehicle Description</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-description" name="description" value={this.state.description} onChange={this.formInputChanged} placeholder="e.g. 2006 Ford Taurus" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group col-25">
                                    <label htmlFor="frm-color">Color</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-color" name="color" value={this.state.color} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group col-50">
                                    <label htmlFor="frm-vin" className="required">VIN</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-vin" name="vin" value={this.state.vin} onChange={this.formInputChanged} placeholder="" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group col-25">
                                    <label htmlFor="frm-tag">Tag</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-tag" name="tag" value={this.state.tag} onChange={this.formInputChanged} placeholder="" autoComplete="off_please_chrome" isRequired={false} />
                                </div>
                                <div className="input-form-group col-25">
                                    <label htmlFor="frm-status">Status</label>
                                    <select id="frm-status" name="status" value={this.state.status} onChange={this.formInputChanged} placeholder="" autoComplete="off_please_chrome">
                                        <option value="0">Hidden</option>
                                        <option value="1">Enabled</option>
                                    </select>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-tracker">Tracker</label>
                                    {
                                        this.state.pendingTrackerId !== null
                                            ? this.state.pendingTrackerId === ""
                                                ? <div className="action-panel-dialog">
                                                    <span>Will unlink tracker.</span>
                                                    <HasRight right="veh:tracker:edit">
                                                        <button type="button" className="button btn-outline" onClick={this.openTrackerLinkDialog}>Link Tracker...</button>
                                                    </HasRight>
                                                </div>
                                                : <div className="action-panel-dialog">
                                                    <span>Will link to <b>{this.state.tracker ? this.state.tracker.displayName : "(pending)"}</b></span>
                                                    <HasRight right="veh:tracker:edit">
                                                        <button type="button" className="button btn-outline btn-inline push-right red" onClick={this.unlinkTracker}>Unlink Tracker...</button>
                                                    </HasRight>
                                                </div>
                                            : !this.state.tracker
                                                ? <div className="action-panel-dialog">
                                                    <span>No tracker linked.</span>
                                                    <HasRight right="veh:tracker:edit">
                                                        <button type="button" className="button btn-outline" onClick={this.openTrackerLinkDialog}>Link Tracker...</button>
                                                    </HasRight>
                                                </div>
                                                : <div className="action-panel-dialog">
                                                    <span>Linked to <b>{this.state.tracker.displayName}</b></span>
                                                    <HasRight right="veh:tracker:edit">
                                                        <button type="button" className="button btn-outline btn-inline push-right red" onClick={this.unlinkTracker}>Unlink Tracker...</button>
                                                    </HasRight>
                                                </div>
                                    }
                                </div>
                            </section>
                        </section>
                        <div className="row-root">
                            <section className="info-block">
                                <header>
                                    <h3>Keys</h3>
                                </header>

                                <ExpandList items={this.state.keys} itemRender={a => `${a.name} (${a.code ? a.code : "TBD"})`} itemIdField="id" hideBlankOption={true} allowOther={true} onAdd={this.addKey} onDelete={this.deleteKey} otherPlaceholderText="e.g. First key, Backup key, etc." />
                                &nbsp;
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-spare-key">Location of spare key</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-spare-key" name="spareKey" value={this.state.spareKey} onChange={this.formInputChanged} placeholder="" autoComplete="off_please_chrome" isRequired={false} />
                                    </div>
                                </section>
                            </section>
                            {
                                this.state.isEdit
                                    ? <section className="info-block">
                                        <header>
                                            <h3>Usage</h3>
                                        </header>
                                        <section>
                                            <div className="input-form-group">
                                                <label htmlFor="frm-checked-out">Checked out by</label>
                                                <select id="frm-checked-out" name="employeeId" value={this.state.employeeId} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                                                    <option value="">Checked In</option>
                                                    <option value="-" disabled={true} />
                                                    {this.state.employees.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                                                </select>
                                            </div>
                                        </section>
                                    </section>
                                    : null
                            }
                        </div>
                    </div>
                    <h3>Last 10 log entries:</h3>
                    <table className="table-vertical">
                        <thead>
                            <tr>
                                <th className="col-smallest">Timestamp</th>
                                <th className="col-smallest">User</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <thead className="table-vertical-head-small">
                            <tr>
                                <th>Vehicle logs</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.log.length === 0
                                ? <td colSpan={10}>No log entries available</td>
                                : this.state.log.map(a => <tr key={a.timestamp}>
                                    <td data-caption="Timestamp: ">{formatDateTimeLocalNumeric(a.timestamp)}</td>
                                    <td data-caption="User: ">{a.userName}</td>
                                    <td data-caption="Action: ">{this.actionToString(a.action)}</td>
                                </tr>)}
                        </tbody>
                    </table>
                    &nbsp;
                    {this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <div className="error-text">{this.state.isSubmitting}</div>
                            : null
                    }
                    {this.state.hasChanged
                        ? <div className="action-panel">
                            <button type="submit" className="button push-right">Save</button>
                            <button type="button" className="button" onClick={this.cancelForm}>Cancel</button>
                        </div>
                        : <div className="action-panel">
                            <button type="button" className="button push-right" onClick={this.cancelForm}>{this.state.isEdit ? "Ok" : "Cancel"}</button>
                        </div>
                    }
                </form>
            </div>
        );
    }

    private async submitForm(e: FormEvent) {
        e.preventDefault();

        let isAllValid = true;
        for (const input of this.inputs) {
            isAllValid = input.validate() && isAllValid;
        }

        if (!isAllValid) {
            return;
        }

        try {
            this.setState({
                isSubmitting: true
            });

            const data = {
                name: this.state.description,
                color: this.state.color,
                vin: this.state.vin,
                tag: this.state.tag,
                keys: this.state.keys,
                status: this.state.status,
                employeeId: this.state.employeeId,
                trackerId: this.state.pendingTrackerId !== null
                    ? this.state.pendingTrackerId
                    : undefined,
                spareKeyNote: this.state.spareKey
            };

            if (this.state.isEdit) {
                await V2.patch(`vehicles/${this.state.id}`, data);
            }
            else {
                await V2.post("vehicles", data);
            }

            this.setState({
                isSubmitting: false,
                hasChanged: false,
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e || "Could not save changes"
            });
        }
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        let name = e.currentTarget.name;
        let value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            hasChanged: true,

            [name]: value
        } as any);
    }

    private addKey(e: ExpandListEvent<any>) {
        const keys = this.state.keys;

        keys.push({
            id: keys.length.toString(),
            name: e.value,
        });

        this.setState({
            keys
        });
    }

    private deleteKey(e: ExpandListEvent<any>) {
        if (e.index === undefined)
            return;

        const keys = this.state.keys;

        keys.splice(e.index, 1);

        this.setState({
            keys
        });
    }

    private actionToString(action: string) {
        switch (action) {
            case "checkout": return "Checked vehicle out";
            case "checkin": return "Checked vehicle in";
            default: return action;
        }
    }

    private showDeletionDialog() {
        this.setState({
            isDeletionDialogOpen: true,
        });
    }

    private closeDeletionDialog() {
        this.setState({
            isDeletionDialogOpen: false,
        });
    }

    private async deleteVehicle() {
        await V2.delete(`vehicles/${this.state.id}`);
    }

    private async openTrackerLinkDialog() {
        try {
            this.setState({
                isLinkDialogOpen: true,
                isLoadingTrackers: true,
            });

            const trackers = await V2.get<any[]>("vehicles/trackers");

            this.setState({
                isLoadingTrackers: false,
                trackers
            });
        }
        catch (e) {
            this.setState({
                isLoadingTrackers: e.message || e || "Could not load trackers"
            });
        }
    }

    private closeTrackerLinkDialog() {
        this.setState({
            isLinkDialogOpen: false
        });
    }

    private async handleTrackerLinkSubmit() {
        const { trackers, selectedTrackerId } = this.state;

        const tracker = trackers.find(a => a.id === selectedTrackerId);
        this.setState({
            hasChanged: true,
            pendingTrackerId: selectedTrackerId,
            tracker
        });
    }

    private unlinkTracker() {
        this.setState({
            hasChanged: true,
            pendingTrackerId: "",
            tracker: undefined
        });
    }
}

export default withTitle("Edit Vehicle", VehicleEditView);